/**
 * Browser side of the non-`.docx` office formats.
 *
 * Thin client over the server `/api/documents/*` endpoints (which run the
 * `office-formats` Rust crate). Spreadsheets (`.xlsx`/`.csv`/`.ods`) speak the
 * **Workbook** model, decks (`.pptx`/`.odp`) the **Presentation** model, and the
 * text-flavoured formats (`.md`/`.txt`/`.odt`) reuse the same rich-text **delta**
 * as `.docx` (see `docx-io.ts`). PDF is export-only here — viewing/annotating is
 * done client-side with `pdf.js`/`pdf-lib`.
 */
import type { DeltaOp } from "../collab/loro-document";
import { readActiveAccountSelector } from "@/lib/accounts";
import { isTauriRuntime } from "@/lib/platform";

/**
 * Same-origin API fetch with the app's cookie-BFF auth: the HttpOnly device
 * cookie rides `credentials: "include"` and the ACTIVE ACCOUNT is selected
 * with the `X-Active-Account` header (without it the backend answers 401 for
 * real accounts — bug caught by the /sheets UI e2e). Tauri sends no selector
 * (native auth injects its own Bearer).
 */
export function officeApiFetch(path: string, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!isTauriRuntime()) {
    const selector = readActiveAccountSelector();
    if (selector) {
      headers.set("X-Active-Account", selector);
    }
  }
  return fetch(path, { ...init, headers, credentials: "include" });
}

const apiFetch = officeApiFetch;

/** A typed spreadsheet value, mirroring `office_formats::CellValue`. */
export type CellValue =
  | { t: "empty" }
  | { t: "number"; v: number }
  | { t: "text"; v: string }
  | { t: "bool"; v: boolean };

export interface Cell {
  value: CellValue;
  formula?: string;
}
export interface Sheet {
  name: string;
  rows: Cell[][];
}
export interface Workbook {
  sheets: Sheet[];
}

/** A slide deck, mirroring `office_formats::Presentation`. */
export interface Slide {
  title?: string;
  body: string[];
}
export interface Presentation {
  slides: Slide[];
}

async function postBinary(path: string, file: Blob): Promise<unknown> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const res = await apiFetch(path, {
    method: "POST",
    headers: { "content-type": "application/octet-stream" },
    body: bytes,
  });
  if (!res.ok) {
    throw new Error(`import failed (${path}): ${res.status}`);
  }
  return res.json();
}

async function postJsonForBlob(path: string, body: unknown): Promise<Blob> {
  const res = await apiFetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(`export failed (${path}): ${res.status}`);
  }
  return res.blob();
}

// --- Spreadsheets (Workbook) ------------------------------------------------

export async function importXlsx(file: Blob): Promise<Workbook> {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- JSON from our own typed API
  return ((await postBinary("/api/documents/import/xlsx", file)) as { workbook: Workbook })
    .workbook;
}
export function exportXlsx(workbook: Workbook): Promise<Blob> {
  return postJsonForBlob("/api/documents/export/xlsx", { workbook });
}
export async function importCsv(file: Blob): Promise<Workbook> {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- JSON from our own typed API
  return ((await postBinary("/api/documents/import/csv", file)) as { workbook: Workbook }).workbook;
}
export function exportCsv(workbook: Workbook): Promise<Blob> {
  return postJsonForBlob("/api/documents/export/csv", { workbook });
}
export async function importOds(file: Blob): Promise<Workbook> {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- JSON from our own typed API
  return ((await postBinary("/api/documents/import/ods", file)) as { workbook: Workbook }).workbook;
}
export function exportOds(workbook: Workbook): Promise<Blob> {
  return postJsonForBlob("/api/documents/export/ods", { workbook });
}

// --- Presentations (Presentation) -------------------------------------------

export async function importPptx(file: Blob): Promise<Presentation> {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- JSON from our own typed API
  return ((await postBinary("/api/documents/import/pptx", file)) as { presentation: Presentation })
    .presentation;
}
export function exportPptx(presentation: Presentation): Promise<Blob> {
  return postJsonForBlob("/api/documents/export/pptx", { presentation });
}
export async function importOdp(file: Blob): Promise<Presentation> {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- JSON from our own typed API
  return ((await postBinary("/api/documents/import/odp", file)) as { presentation: Presentation })
    .presentation;
}
export function exportOdp(presentation: Presentation): Promise<Blob> {
  return postJsonForBlob("/api/documents/export/odp", { presentation });
}

// --- Text-flavoured formats (rich-text delta) -------------------------------

async function importDelta(path: string, file: Blob): Promise<DeltaOp[]> {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- JSON from our own typed API
  return ((await postBinary(path, file)) as { delta: DeltaOp[] }).delta;
}

export const importMarkdown = (file: Blob) => importDelta("/api/documents/import/md", file);
export const exportMarkdown = (delta: DeltaOp[]) =>
  postJsonForBlob("/api/documents/export/md", { delta });
export const importTxt = (file: Blob) => importDelta("/api/documents/import/txt", file);
export const exportTxt = (delta: DeltaOp[]) =>
  postJsonForBlob("/api/documents/export/txt", { delta });
export const importOdt = (file: Blob) => importDelta("/api/documents/import/odt", file);
export const exportOdt = (delta: DeltaOp[]) =>
  postJsonForBlob("/api/documents/export/odt", { delta });

// --- PDF (export/print only; viewing is client-side pdf.js) ------------------

export const exportPdf = (delta: DeltaOp[]) =>
  postJsonForBlob("/api/documents/export/pdf", { delta });
